import {
  buildReferenceRowIndex,
  getReferenceRowFromIndex,
  resolveReferenceRowForOccurrence,
  type ReferenceRowIndexEntry,
  type ReferenceRowLookupOptions,
  type ReferenceRowLookupResult,
} from './reference-row-index';

/**
 * Resolve a chip's `data-ref-id` back to the Reference row it belongs to.
 *
 * Internal row-scoped chips: `{{ref:ref:<rowId>}}` → data-ref-id = `ref:<rowId>`.
 * Legacy internal chips: `{{ref:<targetId>}}` → matches `row.refId`. When
 * several rows share that legacy token (the same target cited more than
 * once from this block), the CANONICALLY FIRST one wins (see
 * `reference-row-order.ts`) — delegates to `getReferenceRowFromIndex` so
 * this and the row-index provider can never disagree about which row that is.
 * URL chips: `{{ref:url:<rowId>}}` → data-ref-id = `url:<rowId>`.
 */
export function resolveChipRefRow<T extends ReferenceRowIndexEntry>(
  refs: T[] | undefined,
  sourceBlockId: string,
  chipRefId: string,
): T | undefined {
  if (!refs) return undefined;
  return getReferenceRowFromIndex(
    buildReferenceRowIndex(refs),
    sourceBlockId,
    chipRefId,
  );
}

/**
 * Occurrence-aware variant of `resolveChipRefRow` — see
 * `resolveReferenceRowForOccurrence` for the exact semantics. Used wherever a
 * chip click must resolve to THAT occurrence's own row rather than always
 * the canonically-first one sharing a legacy token.
 */
export function resolveChipRefRowForOccurrence<T extends ReferenceRowIndexEntry>(
  refs: T[] | undefined,
  sourceBlockId: string,
  chipRefId: string,
  options?: ReferenceRowLookupOptions,
): ReferenceRowLookupResult<T> {
  if (!refs) return { row: undefined, needsOwnRow: false };
  return resolveReferenceRowForOccurrence(
    buildReferenceRowIndex(refs),
    sourceBlockId,
    chipRefId,
    options,
  );
}
